import React, { ReactElement, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import { ScrollbarIndicator } from '@/lib/util';

import { ChoiceOptionType } from '@/modules/types/choice';

import SortableChoice from './SortableChoice';
import Buttons from './Buttons';

import './Sort.scss';

const Sort = ({
  choices,
  handler,
}: {
  choices: ChoiceOptionType[];
  handler: (answer: string) => void;
}): ReactElement => {
  const [choicesState, setChoicesState] = useState<ChoiceOptionType[]>(choices);

  useEffect(() => {
    setChoicesState(choices);
  }, [choices]);

  const reorder = (
    list: ChoiceOptionType[],
    startIndex: number,
    endIndex: number,
  ): ChoiceOptionType[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: DropResult): void => {
    const { source, destination } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const newstate: ChoiceOptionType[] = reorder(
      choicesState,
      source.index,
      destination.index,
    );

    setChoicesState(newstate);
  };

  const onReset = () => {
    setChoicesState(choices);
  };

  const onSubmit = () => {
    const answers = choicesState.map((item) =>
      item.data.lcmsReferenceIds === undefined ||
      Object.keys(item.data.lcmsReferenceIds).length <= 0 ||
      !!item.data.choiceMetadata?.useUUID
        ? item.data.id
        : `${item.data.lcmsReferenceIds.id}`,
    );
    handler(answers.join(','));
  };

  return (
    <div id="Sort" data-testid="sortable" className="elmoca-sort">
      <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
        <div className="elmoca-sort__wrapper" ref={ScrollbarIndicator()}>
          <div className="elmoca-dnd">
            <div className="elmoca-dnd-draglist">
              <Droppable droppableId="sortable-choice">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="elmoca-dnd-draglist-wrapper"
                  >
                    <ol>
                      {choicesState.map((item, index) => (
                        <li key={index}>
                          <SortableChoice item={item.data} index={index} />
                        </li>
                      ))}
                    </ol>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </div>
      </DragDropContext>
      <Buttons isButtonsVisible={true} onReset={onReset} onSubmit={onSubmit} />
    </div>
  );
};

export default Sort;
